import { useContext, useEffect, useState } from 'react';
import MealsContext from '../../context/meals-context';
import MealList from '../../components/Meal/MealList';
import MealItem from '../../components/Meal/MealItem';
import Loader from '../../components/UI/General/Loader';
import IngredientSelector from '../../components/Meal/Ingredient/IngredientSelector';
import TagSelector from '../../components/TagSelector/TagSelector';
import { callApiWithData } from '../../api/call-api';
import Meal, { FilteredMeal, mapMealFromApi } from '../../models/meal';
import { Tag } from '../../models/tag';
import { getTagsByGroup } from '../../api/tag-api';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Box,
  Button,
  Chip,
  Grid,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FilterListIcon from '@mui/icons-material/FilterList';

const YourMeals = () => {
  const mealsContext = useContext(MealsContext);
  const [isFiltering, setIsFiltering] = useState(false);
  const [filteredMeals, setFilteredMeals] = useState<FilteredMeal[]>([]);
  const [filterLoading, setFilterLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tagGroups, setTagGroups] = useState<{ meal_type: Tag[]; cuisine: Tag[] }>({ meal_type: [], cuisine: [] });
  const [tagFiltering, setTagFiltering] = useState(false);
  const [tagFilteredMeals, setTagFilteredMeals] = useState<Meal[]>([]);
  const [tagFilterLoading, setTagFilterLoading] = useState(false);

  useEffect(() => {
    getTagsByGroup().then(setTagGroups);
  }, []);

  const handleSearch = async (ingredientIds: string[]) => {
    setFilterLoading(true);
    setError(null);
    try {
      const response = await callApiWithData(
        '/chef-meals',
        {
          ingredients: ingredientIds,
          sort: 'ingredient-match',
        },
        'POST'
      );

      const meals: FilteredMeal[] = response.meals.map((meal: any) => ({
        ...mapMealFromApi(meal),
        matchedCount: meal.matchedCount ?? 0,
        totalIngredientCount: meal.totalIngredientCount ?? 0,
      }));

      setFilteredMeals(meals);
      setIsFiltering(true);
    } catch {
      setFilteredMeals([]);
      setError('Nepodařilo se vyhledat recepty. Zkuste to prosím znovu.');
    } finally {
      setFilterLoading(false);
    }
  };

  const handleClearFilter = () => {
    setIsFiltering(false);
    setFilteredMeals([]);
    setError(null);
  };

  const handleTagSearch = async (tagIds: string[], group: 'meal_type' | 'cuisine') => {
    setTagFilterLoading(true);
    setError(null);
    try {
      const response = await callApiWithData(
        '/chef-meals',
        {
          tags: { [group]: tagIds },
        },
        'POST'
      );
      const meals: Meal[] = response.meals.map(mapMealFromApi);
      setTagFilteredMeals(meals);
      setTagFiltering(true);
    } catch {
      setError('Nepodařilo se filtrovat podle tagů.');
    } finally {
      setTagFilterLoading(false);
    }
  };

  if (mealsContext.isLoading) {
    return <Loader />;
  }

  return (
    <Box>
      <Accordion sx={{ mb: 2 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <FilterListIcon sx={{ mr: 1 }} />
          <Typography>Filtrovat podle ingrediencí</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <IngredientSelector onSearch={handleSearch} loading={filterLoading} />
          {isFiltering && (
            <Button size="small" onClick={handleClearFilter} sx={{ mt: 1 }}>
              Zrušit filtr
            </Button>
          )}
        </AccordionDetails>
      </Accordion>

      <Accordion sx={{ mb: 2 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <FilterListIcon sx={{ mr: 1 }} />
          <Typography>Filtrovat podle typu jídla</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <TagSelector
            options={tagGroups.meal_type}
            label="Vyber typ jídla"
            onSearch={(ids) => handleTagSearch(ids, 'meal_type')}
            loading={tagFilterLoading}
          />
        </AccordionDetails>
      </Accordion>

      <Accordion sx={{ mb: 2 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <FilterListIcon sx={{ mr: 1 }} />
          <Typography>Filtrovat podle kuchyně</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <TagSelector
            options={tagGroups.cuisine}
            label="Vyber kuchyni"
            onSearch={(ids) => handleTagSearch(ids, 'cuisine')}
            loading={tagFilterLoading}
          />
        </AccordionDetails>
      </Accordion>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {filterLoading && <Loader />}

      {!filterLoading && isFiltering && (
        <Grid container spacing={{ xs: 2, md: 4 }}>
          {filteredMeals.length === 0 && !error && (
            <Grid item xs={12}>
              <Typography variant="body1" color="text.secondary">
                Žádné recepty neodpovídají vybraným ingrediencím.
              </Typography>
            </Grid>
          )}
          {filteredMeals.map((meal) => (
            <Grid key={meal.id} item xs={12} md={6}>
              <Box sx={{ position: 'relative' }}>
                <MealItem meal={meal} />
                {meal.totalIngredientCount > 0 && (
                  <Chip
                    label={`${meal.matchedCount}/${meal.totalIngredientCount} ingrediencí`}
                    color={meal.matchedCount === meal.totalIngredientCount ? 'success' : 'default'}
                    size="small"
                    sx={{ position: 'absolute', top: 8, right: 48 }}
                  />
                )}
              </Box>
            </Grid>
          ))}
        </Grid>
      )}

      {tagFilterLoading && <Loader />}

      {!tagFilterLoading && tagFiltering && (
        <Grid container spacing={{ xs: 2, md: 4 }}>
          {tagFilteredMeals.length === 0 && (
            <Grid item xs={12}>
              <Typography variant="body1" color="text.secondary">
                Žádné recepty neodpovídají vybraným tagům.
              </Typography>
            </Grid>
          )}
          {tagFilteredMeals.map((meal) => (
            <Grid key={meal.id} item xs={12} md={6}>
              <MealItem meal={meal} />
            </Grid>
          ))}
        </Grid>
      )}

      {!filterLoading && !isFiltering && !tagFiltering && !tagFilterLoading && <MealList meals={mealsContext.meals} />}
    </Box>
  );
};

export default YourMeals;
